import { mediaQuery } from '@noths/polaris-client-styles';
import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

// Breakpoints match packages/client-ribbons-base/src/constants/breakpoints.ts.
// Defined locally so this file does not depend on the deprecated
// @noths/polaris-client-ribbons-base grid system.
const BREAKPOINT_M = 768;
const BREAKPOINT_L = 1024;

const { borderRadius, colors, spacing, tint, typography } = themeVariables;

// Row 1: breadcrumbs occupies the left 5 of 12 columns, aligned under
// images, leaving columns 6-12 of row 1 empty. Row 2: images (7 cols) and
// the title column (5 cols) split the row. Named so the shape is visible
// in one place, rather than composed from per-element span/row classes.
export const grid = css`
  display: flex;
  flex-direction: column;
  gap: var(${spacing.md});

  ${mediaQuery(
    { min: BREAKPOINT_M },
    css`
      display: grid;
      grid-template-columns: repeat(12, 1fr);
      column-gap: var(${spacing.xl});
      grid-template-areas:
        'breadcrumbs breadcrumbs breadcrumbs breadcrumbs breadcrumbs . . . . . . .'
        'images images images images images images images title title title title title';
    `,
  )}
`;

export const breadcrumbsArea = css`
  ${mediaQuery(
    { min: BREAKPOINT_M },
    css`
      grid-area: breadcrumbs;
    `,
  )}
`;

export const imagesArea = css`
  ${mediaQuery(
    { min: BREAKPOINT_M },
    css`
      grid-area: images;
    `,
  )}
`;

export const titleArea = css`
  display: flex;
  flex-direction: column;
  gap: var(${spacing.lg});

  ${mediaQuery(
    { min: BREAKPOINT_M },
    css`
      grid-area: title;
    `,
  )}
`;

// titleArea's flex column stretches children full-width by default;
// OfferLabel is meant to hug its own content (it's already inline-block).
export const offerLabel = css`
  align-self: flex-start;
`;

// ctaTrigger bundles delivery summary, quantity/total, and the basket
// button; this spaces those apart, independent of it being a grid item.
export const ctaTriggerStack = css`
  display: flex;
  flex-direction: column;
  gap: var(${spacing.lg});
`;

export const maxWidthContainer = css`
  margin: var(${spacing.md}) auto var(${spacing.xxl});
  padding: var(${spacing.sm}) var(${spacing.md});

  ${mediaQuery(
    { min: BREAKPOINT_L },
    css`
      padding-left: var(${spacing.xl});
      padding-right: var(${spacing.xl});
      max-width: 1210px;
    `,
  )}
`;

export const productReviewsWrapper = css`
  ${mediaQuery(
    { min: BREAKPOINT_M },
    css`
      grid-row: 1;
      grid-column: 1 / span 6;
    `,
  )}

  ${mediaQuery(
    { min: BREAKPOINT_L },
    css`
      grid-column: 1 / span 8;
    `,
  )}
`;

export const productTitle = css`
  margin: 0;
  font-family: var(${typography.heading.fontFamily});
  font-size: var(${typography.heading.sizes.md.fontSize});
  line-height: var(${typography.heading.sizes.md.lineHeight});
  font-weight: var(${typography.heading.fontWeight});
  color: var(${colors.content.default});
  letter-spacing: var(${typography.heading.letterSpacing});
`;

export const productDetailsWrapper = css`
  display: flex;
  flex-direction: column;
  row-gap: var(${spacing.xxl});

  ${mediaQuery(
    { min: BREAKPOINT_M },
    css`
      display: grid;
      grid-template-columns: 1fr 1fr;
      column-gap: var(${spacing.xxl});
      grid-template-areas: 'productInformation publicAttributes';

      /* productInformation is the only child when publicAttributes isn't rendered;
         let it take the full row instead of leaving the other column empty. */
      &:has(> :only-child) {
        grid-template-columns: 1fr;
        grid-template-areas: 'productInformation';
      }
    `,
  )}
`;

export const publicAttributesArea = css`
  ${mediaQuery(
    { min: BREAKPOINT_M },
    css`
      grid-area: publicAttributes;
    `,
  )}
`;

export const productInformationArea = css`
  ${mediaQuery(
    { min: BREAKPOINT_M },
    css`
      grid-area: productInformation;
    `,
  )}
`;

export const column = css`
  display: flex;
  flex-direction: column;
  gap: var(${spacing.xxl});

  ${mediaQuery(
    { min: BREAKPOINT_M },
    css`
      column-gap: var(${spacing.xxxl});
    `,
  )}
`;

export const reviewsSummaryWrapper = css`
  margin-bottom: var(${spacing.lg});
`;

export const overallRatingWrapper = css`
  display: flex;
  flex-direction: column;
  margin-bottom: var(${spacing.lg});
`;

export const descriptionArea = css`
  p {
    margin: 0;
    font-size: var(${typography.body.sizes.md.fontSize});
    line-height: var(${typography.body.sizes.md.lineHeight});
    letter-spacing: var(${typography.body.letterSpacing});
    font-weight: var(${typography.body.fontWeight});
    color: var(${colors.content.secondary});
  }
`;

export const priceRow = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(${spacing.xxs});
`;

export const ratingPill = css`
  flex: 0 0 auto;
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border: 1px solid var(${colors.stroke.default});
  border-radius: var(${borderRadius.md.desktop});
  background-color: var(${colors.surface.default});
  box-shadow: 0 0 4px 0
    color-mix(in srgb, var(${colors.content.default}) var(${tint.hover}), transparent);
  overflow: hidden;
  white-space: nowrap;
  text-align: right;
  font-family: var(${typography.body.fontFamily});

  > * {
    padding: var(${spacing.sm});
  }

  > * + * {
    border-left: 1px solid var(${colors.stroke.default});
  }
`;

// Reserves space for the lazily-rendered PartnerMiniModule so revealing it does
// not shift layout near the desktop fold. Height approximates the module (badge
// header + logo row + info strip); the tint matches the module's card.
export const partnerMiniModulePlaceholder = css`
  min-height: 180px;
  border-radius: var(${borderRadius.md.desktop});
  background-color: color-mix(in srgb, var(${colors.content.link}) 5%, white);
`;
